import {usePromotion} from '@/context/Promotion/PromotionContext'
import {FreeProductRewardFromList, Item, PromotionCondition, Reward} from '@/types/promotion'
import {BuyType} from '@/utils/constants/promotionConstants'
import {useMemo, useState} from 'react'

type SelectionTarget = {type: 'mandatory'} | {type: 'list'; index: number}

type UseRewardFreeProductListsArgs = {
  promotionCondition: PromotionCondition
  rewardIndex?: number
  selectedPromotionCondition?: string
}

export const useRewardFreeProductLists = ({
  promotionCondition,
  rewardIndex = 0,
  selectedPromotionCondition,
}: UseRewardFreeProductListsArgs) => {
  const {promotionConfig, isLoadingConfig, updatePromotion} = usePromotion()
  const reward = (promotionCondition?.rewards?.[rewardIndex] ?? {}) as FreeProductRewardFromList
  const [selectionTarget, setSelectionTarget] = useState<SelectionTarget | null>(null)
  const [duplicateSelectionTarget, setDuplicateSelectionTarget] = useState<SelectionTarget | null>(null)
  const freeProductLists = reward.freeProductLists ?? []
  const isMandatoryProductsEnabled = reward.mandatoryProductsEnabled ?? false
  const productTypeOptions = [{displayText: 'Add Product(s)', id: BuyType.PRODUCT_ID}]
  const buyQtyOptions = useMemo(
    () => promotionConfig?.buyQty?.map(value => ({displayText: value, id: Number(value)})),
    [promotionConfig?.buyQty]
  )

  const updateReward = (updates: Partial<FreeProductRewardFromList>) => {
    updatePromotion(prev => ({
      ...prev,
      promotionConditions: prev.promotionConditions.map(condition => ({
        ...condition,
        rewards: condition.rewards?.map((currentReward, index) =>
          index === rewardIndex ? ({...currentReward, ...updates} as Reward) : currentReward
        ),
      })),
    }))
  }

  const getItems = (target: SelectionTarget) =>
    target.type === 'mandatory' ? (reward.mandatoryFreeProducts ?? []) : (freeProductLists[target.index]?.items ?? [])

  const targetsMatch = (first: SelectionTarget | null, second: SelectionTarget) =>
    first?.type === second.type && (first.type !== 'list' || (second.type === 'list' && first.index === second.index))

  const getOtherProductIds = (target: SelectionTarget) => {
    const items = [
      ...(target.type !== 'mandatory' && isMandatoryProductsEnabled ? mandatoryProducts : []),
      ...freeProductLists.flatMap((list, index) =>
        target.type === 'list' && target.index === index ? [] : (list.items ?? [])
      ),
    ]
    return new Set(items.map(item => item.id))
  }

  const selectionHasDuplicateProducts = (target: SelectionTarget, items: Item[]) => {
    const otherProductIds = getOtherProductIds(target)
    const selectedIds = items.map(item => item.id)
    return new Set(selectedIds).size !== selectedIds.length || selectedIds.some(id => otherProductIds.has(id))
  }

  const getProductsValue = (items: Item[] = []) => items.map(item => ({id: item.id, displayText: item.name}))
  const getSelectedText = (items: Item[] = []) => items.map(item => item.name).join(', ')

  const handleMandatoryProductsToggle = (enabled: boolean) => {
    setDuplicateSelectionTarget(null)
    updateReward({
      mandatoryProductsEnabled: enabled,
      ...(!enabled && {mandatoryQuantity: null, mandatoryFreeProducts: []}),
    })
  }

  const handleMandatoryQuantity = values => {
    const quantity = Number(values?.[0]?.id)
    updateReward({mandatoryQuantity: Number.isInteger(quantity) && quantity > 0 ? quantity : null})
  }

  const handleListQuantity = (index: number, values) => {
    const quantity = Number(values?.[0]?.id)
    const nextLists = freeProductLists.map((list, listIndex) =>
      listIndex === index ? {...list, quantity: Number.isInteger(quantity) && quantity > 0 ? quantity : null} : list
    )
    updateReward({freeProductLists: nextLists})
  }

  const handleProductSelection = (values, target: SelectionTarget) => {
    if (!values?.length) {
      setDuplicateSelectionTarget(null)
      if (target.type === 'mandatory') {
        updateReward({mandatoryFreeProducts: []})
      } else {
        updateReward({
          freeProductLists: freeProductLists.map((list, index) =>
            index === target.index ? {...list, items: []} : list
          ),
        })
      }
      return
    }

    if (values.some(item => item.id === BuyType.PRODUCT_ID)) {
      setSelectionTarget(target)
      return
    }

    const items = values.map(item => ({
      id: item.id,
      name: item.displayText,
      uom: selectedPromotionCondition || promotionCondition?.conditionGroup?.conditions?.[0]?.items?.[0]?.uom || 'UN',
    }))
    updateSelectedProducts(target, items)
  }

  const updateSelectedProducts = (target: SelectionTarget, items: Item[]) => {
    if (selectionHasDuplicateProducts(target, items)) {
      setDuplicateSelectionTarget(target)
      return
    }

    setDuplicateSelectionTarget(null)
    if (target.type === 'mandatory') {
      updateReward({mandatoryFreeProducts: items})
      return
    }

    updateReward({
      freeProductLists: freeProductLists.map((list, index) => (index === target.index ? {...list, items} : list)),
    })
  }

  const handleSubmitModal = selectedTags => {
    if (!selectionTarget) return
    const values = Array.isArray(selectedTags) ? selectedTags : (selectedTags?.values ?? [])
    const uom =
      selectedPromotionCondition || promotionCondition?.conditionGroup?.conditions?.[0]?.items?.[0]?.uom || 'UN'
    updateSelectedProducts(
      selectionTarget,
      values.map(item => ({id: item.id, name: item.displayText, uom}))
    )
    setSelectionTarget(null)
  }

  const addList = () => {
    const existingGroupNames = new Set(freeProductLists.map(list => list.groupName))
    let letterIndex = 0
    let groupName
    do {
      groupName = `List ${String.fromCharCode(65 + letterIndex)}`
      letterIndex += 1
    } while (existingGroupNames.has(groupName))

    updateReward({
      freeProductLists: [...freeProductLists, {groupName, items: []}],
    })
  }

  const removeList = index => {
    setDuplicateSelectionTarget(null)
    updateReward({freeProductLists: freeProductLists.filter((_, listIndex) => listIndex !== index)})
  }

  const mandatoryProducts = reward.mandatoryFreeProducts ?? []
  const modalItems = selectionTarget ? getItems(selectionTarget) : []
  const isFreeProductListComplete = list =>
    typeof list?.quantity === 'number' &&
    list.quantity > 0 &&
    Array.isArray(list.items) &&
    list.items.length > 0 &&
    list.items.every(item => item?.id && item?.name && item?.uom)
  const hasCompleteList = freeProductLists.some(isFreeProductListComplete)
  const duplicateProductIds = (() => {
    const productIds = [
      ...(isMandatoryProductsEnabled ? mandatoryProducts : []),
      ...freeProductLists.flatMap(list => list.items ?? []),
    ].map(item => item.id)
    const seen = new Set<string>()
    const duplicates = new Set<string>()
    productIds.forEach(id => {
      if (seen.has(id)) duplicates.add(id)
      seen.add(id)
    })
    return duplicates
  })()
  const listHasInput = index => {
    const list = freeProductLists[index]
    return list?.quantity != null || (list?.items?.length ?? 0) > 0
  }
  const isListRequired = index =>
    listHasInput(index) || (!isMandatoryProductsEnabled && !hasCompleteList && index === 0)
  const listHasDuplicateProduct = index =>
    (freeProductLists[index]?.items ?? []).some(item => duplicateProductIds.has(item.id))

  return {
    isLoadingConfig,
    buyQtyOptions,
    productTypeOptions,
    freeProductLists,
    isMandatoryProductsEnabled,
    mandatoryQuantityValue: buyQtyOptions?.filter(option => option.id === reward.mandatoryQuantity) ?? [],
    mandatoryProductsValue: getProductsValue(mandatoryProducts),
    mandatoryProductsText: getSelectedText(mandatoryProducts),
    isMandatoryQuantityError:
      isMandatoryProductsEnabled && !(typeof reward.mandatoryQuantity === 'number' && reward.mandatoryQuantity > 0),
    isMandatoryProductsError:
      isMandatoryProductsEnabled &&
      (mandatoryProducts.length === 0 ||
        mandatoryProducts.some(item => duplicateProductIds.has(item.id)) ||
        targetsMatch(duplicateSelectionTarget, {type: 'mandatory'})),
    mandatoryProductsErrorMessage:
      mandatoryProducts.some(item => duplicateProductIds.has(item.id)) ||
      targetsMatch(duplicateSelectionTarget, {type: 'mandatory'})
        ? 'Products must be unique'
        : 'Required',
    modalProps: {show: Boolean(selectionTarget), type: selectionTarget ? BuyType.PRODUCT_ID : ''},
    modalConditionValues: {
      itemType: BuyType.PRODUCT_ID,
      values: getProductsValue(modalItems),
    },
    getListQuantityValue: index =>
      buyQtyOptions?.filter(option => option.id === freeProductLists[index]?.quantity) ?? [],
    getListProductsValue: index => getProductsValue(freeProductLists[index]?.items),
    getListProductsText: index => getSelectedText(freeProductLists[index]?.items),
    isListQuantityRequired: isListRequired,
    isListProductsRequired: isListRequired,
    isListQuantityError: index => {
      const quantity = freeProductLists[index]?.quantity
      return isListRequired(index) && !(typeof quantity === 'number' && quantity > 0)
    },
    isListProductsError: index =>
      listHasDuplicateProduct(index) ||
      targetsMatch(duplicateSelectionTarget, {type: 'list', index}) ||
      (isListRequired(index) && (freeProductLists[index]?.items?.length ?? 0) === 0),
    getListProductsErrorMessage: index =>
      listHasDuplicateProduct(index) || targetsMatch(duplicateSelectionTarget, {type: 'list', index})
        ? 'Products must be unique'
        : 'Required',
    handleMandatoryProductsToggle,
    handleMandatoryQuantity,
    handleListQuantity,
    handleMandatoryProductsChange: values => handleProductSelection(values, {type: 'mandatory'}),
    handleListProductsChange: (index, values) => handleProductSelection(values, {type: 'list', index}),
    handleSubmitModal,
    handleCloseModal: () => setSelectionTarget(null),
    addList,
    removeList,
  }
}
